import { useCallback, useEffect, useState } from 'react';

import { FareType, PassType } from '../../../../utils/types';
import { EurailSearchFormProps } from './types';
import { EurailTravellersCount, useTravellersSelector } from './useTravellersSelector';

const DEFAULT_INITIAL_TRAVELLERS_COUNT = 1;
export type UseEurailSearchFormParams = Pick<
  EurailSearchFormProps,
  'onSubmit' | 'searchInput' | 'onError'
>;

const areAllPassengersZero = (input: Partial<EurailTravellersCount>): boolean => {
  return (
    (!input.adult || input.adult === 0) &&
    (!input.senior || input.senior === 0) &&
    (!input.child || input.child === 0) &&
    (!input.youth || input.youth === 0)
  );
};

const getInitialTravellersCount = (
  input: Partial<EurailTravellersCount>,
): EurailTravellersCount => {
  const allZero = areAllPassengersZero(input);
  return {
    adult: input.adult ?? 0,
    senior: input.senior ?? 0,
    child: input.child ?? 0,
    youth: allZero ? DEFAULT_INITIAL_TRAVELLERS_COUNT : input.youth ?? 0,
  };
};

const isFormValidCheck = (
  to: string | undefined,
  pass: PassType | undefined,
  travellersCount: EurailTravellersCount,
): boolean => {
  const hasDestination = pass !== PassType.ONE_COUNTRY || !!to;
  const hasTravellers = Object.values(travellersCount).some((count) => count > 0);

  const { adult, youth, senior, child } = travellersCount;
  const onlyChildrenSelected = child > 0 && adult === 0 && youth === 0 && senior === 0;

  return hasDestination && hasTravellers && !onlyChildrenSelected;
};

export const useEurailSipSearchForm = ({ searchInput, onSubmit }: UseEurailSearchFormParams) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [travellersCount, setTravellersCount] = useState<EurailTravellersCount>(
    getInitialTravellersCount(searchInput),
  );
  const [to, setTo] = useState<string | undefined>(searchInput.to ?? undefined);
  const [fareClass, setFareClass] = useState<string>(
    searchInput.fareClass ?? FareType.SECOND_CLASS,
  );

  const { travellerIncrements } = useTravellersSelector(travellersCount);

  const travellersFields = travellerIncrements?.map((traveller) => ({
    ...traveller,
    value: travellersCount[traveller.id as keyof EurailTravellersCount] || 0,
    onChange: (value: number) => {
      setTravellersCount({ ...travellersCount, [traveller.id]: value });
    },
  }));

  useEffect(() => {
    setIsFormValid(isFormValidCheck(to, searchInput.pass, travellersCount));
  }, [to, searchInput.pass, travellersCount]);

  const handleOnSubmitButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!isFormValid) return;

      onSubmit({
        to,
        fareClass,
        pass: searchInput.pass ?? PassType.GLOBAL,
        adult: travellersCount.adult,
        youth: travellersCount.youth,
        child: travellersCount.child,
        senior: travellersCount.senior,
      });
    },
    [travellersCount, onSubmit, isFormValid, fareClass, searchInput.pass, to],
  );
  return {
    setIsFormValid,
    setTravellersCount,
    pass: searchInput.pass,
    setPass: searchInput.setPass,
    to,
    setTo,
    fareClass,
    setFareClass,
    travellersFields,
    handleOnSubmitButtonClick,
    isFormValid,
  };
};
